import { useCallback } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { doseVisibilityState } from '~/state/atoms/dose/doseAtoms';
import { environmentState } from '~/state/atoms/engineAtoms';

type UseDoseVisibilityResult = {
    isDoseVisible: boolean;
    toggleDoseVisibility: () => void;
};

/**
 * Shared hook for the dose visibility on/off toggle. Multiple surfaces
 * (sidebar header, viewport overlay) drive the same visibility, so the
 * "flip atom AND tell the engine" invariant must live in exactly one
 * place — otherwise an engine call could be skipped and the rendered
 * dose state would drift from the UI state.
 */
export default function useDoseVisibility(): UseDoseVisibilityResult {
    const [isDoseVisible, setIsDoseVisible] =
        useRecoilState(doseVisibilityState);
    const environment = useRecoilValue(environmentState);

    const toggleDoseVisibility = useCallback(() => {
        if (!environment) {
            return;
        }
        const nextVisibility = !isDoseVisible;
        setIsDoseVisible(nextVisibility);
        environment.updateDoseVisibility(nextVisibility);
    }, [environment, isDoseVisible, setIsDoseVisible]);

    return { isDoseVisible, toggleDoseVisibility };
}
